// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { useCallback } from "react";
import { createSelectLabelId } from "store/dgml/dgmlSelectors";
import { useLockFeedbackMutation, useUnlockFeedbackMutation } from "store/frontend/frontendSlice";
import { useAppSelector } from "./redux";
import { selectFeedbackLock } from "store/frontend/selectors";
import { selectLabelChunks } from "store/reports/selectors";

interface ILabelConfirmation {
    isConfirmed: boolean;
    isConfirming: boolean;
    isEnabled: boolean;
    confirm: (spanIds?: string[]) => void;
    unconfirm: () => void;
}

const selectLabelId = createSelectLabelId();
export function useConfirmLabel(documentId: string, label: string): ILabelConfirmation {
    const { clusterId: docsetId, fileId } = createFolderId(documentId);
    const labelId = useAppSelector(state => selectLabelId(state, docsetId, label ?? '')) ?? '';
    const labelSpanIds = useAppSelector(state => selectLabelChunks(state, label, documentId));

    const isLocked = useAppSelector(state => !!selectFeedbackLock(state, docsetId, fileId, labelId));

    const [triggerLock, lockData] = useLockFeedbackMutation();
    const [triggerUnlock] = useUnlockFeedbackMutation();

    const lock = useCallback((spanIds?: string[]) => {
        if (!fileId || !label) {
            return;
        }

        const targetIds = (spanIds ?? labelSpanIds).filter(x => !!x);
        triggerLock({
            docsetId: docsetId,
            items: [{
                fileId: fileId,
                labelId: labelId,
                label: label,
                spanIds: targetIds
            }]
        });
    }, [triggerLock, docsetId, fileId, labelId, label, labelSpanIds]);

    const unlock = useCallback(() => {
        if (!fileId || !labelId) {
            return;
        }

        triggerUnlock({
            docsetId: docsetId,
            items: [{
                fileId: fileId,
                labelId: labelId
            }]
        });
    }, [triggerUnlock, docsetId, fileId, labelId]);

    return {
        isConfirmed: isLocked,
        isConfirming: lockData.isLoading,
        isEnabled: true,
        confirm: lock,
        unconfirm: unlock
    }
}